// One aggregated view of every tune the app can load — presets, bundled native
// Scores, saved Scores, the openbook melody corpus, user playlists and the loaded iReal library —
// shared by the ⌘K palette and the Tunes browser so the two can never disagree
// about what exists or how it loads. Entries are plain data plus a headless
// `load()` that goes through the store imperatively (the command-layer
// pattern), so consumers only decide presentation and navigation.

import { loadOpenbookScore, scoreHasMelody, type OpenbookLibraryEntry } from './openbookLibrary';
import { loadRealbookScore, type RealbookLibraryEntry } from './realbookLibrary';
import { loadLibraryScore, type LibraryTuneEntry } from './libraryTunes';
import { loadAccountTune } from './sharedTunes';
import { BUNDLED_SCORES, bundledScoreById, loadBundledScore } from './bundledScores';
import { playlistDisplayName } from './bundledPlaylists';
import { historyView, recentsView } from '@/state/historyView';
import { mostPlayedShelf } from '@/state/tuneHistory';
import { lovedTunes, type TasteMark } from '@/state/tuneTaste';
import { latestPlaylistScoreRef } from './playlistScoreUpdates';
import { scoreCatalogKey, type ScoreCatalogEntry } from './scoreCatalog';
import { scoreHasLyrics } from '@/music/score/toSong';
import { recordEvent } from '@/analytics/usage';
import type { IRealIndex } from '@/music/ireal/parse';
import { isAbcScheme, peekTuneMeta } from '@/music/tuneIndex';
import { keyLabelOf } from '@/music/score/convert';
import type { Score } from '@/music/score/types';
import type { Song } from '@/music/song';
import {
  playlistRefKey,
  useStore,
  type PlaylistDoc,
  type PlaylistTuneRef,
  type UserPlaylist,
} from '@/state/store';

/** Where a tune comes from. The strings double as the palette's badge text
 * ('tune' = the loaded iReal library, its historical label; 'abc' = a tune of
 * a loaded ABC tunebook, the other kind of loaded list). */
export type TuneSourceKind =
  | 'preset'
  | 'native'
  | 'saved'
  | 'openbook'
  | 'realbook'
  | 'library'
  | 'shared'
  | 'playlist'
  | 'tune'
  | 'abc';

export interface TuneSourceEntry {
  /** Stable unique key across the whole aggregation. */
  key: string;
  title: string;
  composer?: string;
  /** Extra context beyond the composer (e.g. the owning playlist's name). */
  context?: string;
  source: TuneSourceKind;
  /** The tune carries a written melody (openbook / a saved Score with notes). */
  melody: boolean;
  /** The tune carries sung words. Corpus rows read a manifest flag (no score
   * is fetched to answer this); saved and bundled Scores are inspected. A
   * chord-only source is always false — an iReal chunk has nowhere to put a
   * lyric. */
  lyrics: boolean;
  /** Feel label ("Medium Swing") where the source knows it — iReal chunk
   * headers (peeked without unscrambling) and saved Scores. */
  style?: string;
  /** Prettified musical key ("C minor") where the source knows it. */
  musicKey?: string;
  /** How this tune would be stored in a user playlist — present on every
   * addable row (everything except demo presets), so the browser's Add-to
   * works for iReal, openbook and saved tunes alike. */
  ref?: PlaylistTuneRef;
  /** Owning user playlist for source 'playlist' (browser scoping). */
  playlistId?: string;
  /** Which ROW of that playlist this is. Reorder and remove address a row, not
   * a tune — a set may hold the same tune twice. */
  playlistEntryId?: string;
  /** The row's performance note, where one is set. */
  note?: string;
  /**
   * Whether this row's reference still points at something, WITHOUT fetching.
   *
   * Only set for playlist rows, where the question means anything: a corpus
   * row exists because the corpus listed it. `'unknown'` is a real answer
   * rather than a shrug — the manifests arrive asynchronously, and before they
   * land every corpus row would otherwise look missing. A set that flashes
   * "unavailable" across itself on every load would be worse than one that
   * waits a beat before saying anything.
   */
  resolvable?: RefResolvability;
  /** The stored score version has gone, but an explicit update is available. */
  scoreUpdate?: Extract<PlaylistTuneRef, { kind: 'score' }>;
  /** Load into the store; resolves to the loaded title, throws on failure. */
  load: () => Promise<string>;
}

/**
 * `'missing'` is a strong signal, not proof: for a corpus row it means the
 * manifest no longer lists that id, and the file could in principle still be
 * sitting there. That is why it MARKS a row rather than disabling it — the
 * player keeps the option to try, and `loadFirstPlayable` handles the real
 * failure at the moment it actually matters.
 */
export type RefResolvability = 'ok' | 'missing' | 'unknown';

export interface TuneSourceInputs {
  presets: Song[];
  /** Defaults to the active app catalogue; injectable for focused tests. */
  bundledScores?: readonly ScoreCatalogEntry[];
  savedCharts: Score[];
  openbook: OpenbookLibraryEntry[];
  /** The realbook.site corpus (validated manifest); [] until fetched. */
  realbook?: RealbookLibraryEntry[];
  /** Tunes an admin promoted to the library; [] until fetched. */
  library?: LibraryTuneEntry[];
  userPlaylists: UserPlaylist[];
  playlist: IRealIndex | null;
}

/**
 * Does the corpus still list this id?
 *
 * An empty manifest means "not fetched yet", not "the corpus is empty" — both
 * loaders memoize a fetch that resolves after first paint, and treating the
 * gap as absence would paint every corpus row in a set as missing for the
 * moment before the manifest lands.
 */
function manifestHas(manifest: readonly { id: string }[], id: string): RefResolvability {
  if (manifest.length === 0) return 'unknown';
  return manifest.some((e) => e.id === id) ? 'ok' : 'missing';
}

async function loadLibraryRow(id: string, title: string): Promise<string> {
  let score: Awaited<ReturnType<typeof loadLibraryScore>>;
  try {
    score = await loadLibraryScore({ id });
  } catch {
    throw new Error('Could not load that tune.');
  }
  useStore.getState().loadScore(score);
  return score.title || title;
}

function loadChunk(scheme: string, chunk: string): Promise<string> {
  const title = useStore.getState().loadTuneFromChunk(scheme, chunk);
  if (!title) return Promise.reject(new Error('Could not read that song.'));
  return Promise.resolve(title);
}

/**
 * Flatten every source into one list. Pure with respect to its inputs (pass
 * the store slices you subscribe to); `load()` closes over the store's
 * imperative actions. Order groups sources: presets, native Scores, saved, openbook,
 * playlists, then the loaded library — consumers re-rank by search anyway.
 */
export function collectTuneSources(inputs: TuneSourceInputs): TuneSourceEntry[] {
  const out: TuneSourceEntry[] = [];

  for (const preset of inputs.presets) {
    out.push({
      key: `preset-${preset.id}`,
      title: preset.title,
      source: 'preset',
      melody: false,
      lyrics: false,
      musicKey: preset.key,
      load: () => {
        useStore.getState().loadSong(preset);
        return Promise.resolve(preset.title);
      },
    });
  }

  for (const entry of inputs.bundledScores ?? BUNDLED_SCORES) {
    if (entry.listing.status !== 'active') continue;
    const { summary } = entry;
    out.push({
      key: scoreCatalogKey(entry),
      title: summary.title,
      composer: summary.composer,
      source: 'native',
      melody: summary.hasMelody,
      lyrics: summary.hasLyrics ?? false,
      style: summary.style,
      musicKey: summary.musicKey,
      ref: {
        kind: 'score',
        id: entry.record.id,
        revisionId: entry.revision.id,
        title: summary.title,
        composer: summary.composer,
      },
      load: async () => {
        useStore.getState().loadScore(await loadBundledScore(entry));
        return summary.title;
      },
    });
  }

  for (const saved of inputs.savedCharts) {
    out.push({
      key: `saved-${saved.id}`,
      title: saved.title,
      composer: saved.composer,
      source: 'saved',
      melody: scoreHasMelody(saved),
      lyrics: scoreHasLyrics(saved),
      style: saved.style,
      musicKey: keyLabelOf(saved.form[0]?.keySig),
      ref: saved.id
        ? { kind: 'saved', id: saved.id, title: saved.title, composer: saved.composer }
        : undefined,
      load: () => {
        useStore.getState().loadScore(saved);
        return Promise.resolve(saved.title);
      },
    });
  }

  for (const entry of inputs.openbook) {
    out.push({
      key: `ob-${entry.id}`,
      title: entry.title,
      composer: entry.composer,
      source: 'openbook',
      melody: true,
      lyrics: entry.lyrics ?? false,
      ref: {
        kind: 'openbook',
        id: entry.id,
        file: entry.file,
        title: entry.title,
        composer: entry.composer,
      },
      load: async () => {
        try {
          useStore.getState().loadScore(await loadOpenbookScore(entry));
        } catch {
          throw new Error('Could not load that tune.');
        }
        return entry.title;
      },
    });
  }

  for (const entry of inputs.realbook ?? []) {
    out.push({
      key: `rb-${entry.id}`,
      title: entry.title,
      composer: entry.composer,
      source: 'realbook',
      // The manifest lists only importer-validated lead sheets, and a lead
      // sheet without a melody is a chart — realbook doesn't publish those.
      melody: true,
      lyrics: entry.lyrics ?? false,
      musicKey: entry.key,
      ref: {
        kind: 'realbook',
        id: entry.id,
        file: entry.file,
        title: entry.title,
        composer: entry.composer,
      },
      load: async () => {
        try {
          useStore.getState().loadScore(await loadRealbookScore(entry));
        } catch {
          throw new Error('Could not load that tune.');
        }
        return entry.title;
      },
    });
  }

  for (const entry of inputs.library ?? []) {
    out.push({
      key: `library-${entry.id}`,
      title: entry.title,
      composer: entry.composer,
      context: entry.credit,
      source: 'library',
      melody: entry.melody,
      lyrics: false,
      style: entry.style,
      ref: { kind: 'library', id: entry.id, title: entry.title, composer: entry.composer },
      load: () => loadLibraryRow(entry.id, entry.title),
    });
  }

  for (const list of inputs.userPlaylists) {
    for (const entry of list.entries) {
      out.push({
        ...playlistEntryRow(entry.ref, inputs),
        // The row's own id, so the key survives a reorder and stays distinct
        // when the same tune appears twice in one set.
        key: `pl-${list.id}-${entry.entryId}`,
        context: list.name,
        source: 'playlist',
        playlistId: list.id,
        playlistEntryId: entry.entryId,
        ...(entry.note ? { note: entry.note } : {}),
      });
    }
  }

  if (inputs.playlist) {
    const { scheme, name, songs } = inputs.playlist;
    songs.forEach((song, i) => {
      const meta = peekTuneMeta(scheme, song.chunk);
      out.push({
        key: `lib-${i}-${song.title}`,
        title: song.title,
        composer: song.composer || undefined,
        context: playlistDisplayName(name) ?? 'loaded playlist',
        source: isAbcScheme(scheme) ? 'abc' : 'tune',
        melody: meta.melody,
        lyrics: meta.lyrics,
        style: meta.style,
        musicKey: meta.key,
        ref: {
          kind: 'ireal',
          title: song.title,
          composer: song.composer ?? '',
          scheme,
          chunk: song.chunk,
        },
        load: () => loadChunk(scheme, song.chunk),
      });
    });
  }

  // Every load that has an identity feeds the recents shelf — ONE seam, so
  // the palette, the Tunes browser and the home page all record the same way
  // (and re-opening from Recents bumps the tune back to the top).
  return out.map((entry) => (entry.ref ? withRecentRecording(entry) : entry));
}

/**
 * Record the load: the recents shelf always, and the playlist position and
 * the row's call when the row came from a playlist.
 *
 * Both ride the same wrapper for the same reason — the palette, the Tunes
 * browser and the Playlists page all go through here, so opening the third
 * tune of a set moves the cursor identically whichever surface you opened it
 * from. A cursor recorded at the call sites instead would be right on the page
 * that remembered and silently wrong everywhere else.
 */
function withRecentRecording(entry: TuneSourceEntry): TuneSourceEntry {
  return {
    ...entry,
    load: async () => {
      const title = await entry.load();
      const store = useStore.getState();
      // The ONE load seam records usage too (#324) — palette, browser, home
      // shelf and setlists all pass through here, so the event cannot
      // disagree with the recents it stands beside.
      recordEvent('tune_loaded', { kind: entry.ref?.kind, source: entry.source, title });
      if (entry.ref) {
        store.recordRecentTune(entry.ref);
        // Must come AFTER the load: the load writes the score, and any musical
        // write clears the ref. Recording it here is what makes "the tune on
        // screen is exactly what this reference names" true.
        store.noteCurrentTuneRef(entry.ref);
      }
      if (entry.playlistId && entry.playlistEntryId) {
        store.enterSetAt(entry.playlistId, entry.playlistEntryId);
        // Also after the load, which cleared the last tune's source: the tune
        // now plays with this row's call, if it has one.
        store.openedFromPlaylist(entry.playlistId, entry.playlistEntryId);
      }
      return title;
    },
  };
}

/** The recents shelf as loadable rows — the home page's data. Rows resolve
 *  through the same per-kind machinery as playlist entries, so a deleted
 *  saved tune reports its absence instead of rendering a dead card. */
export function recentTuneRows(inputs: TuneSourceInputs): TuneSourceEntry[] {
  const s = useStore.getState();
  return recentsView(s.recentTunes, s.tuneHistory, s.otherHistory).map((ref) =>
    historyRow(ref, inputs, 'recent'),
  );
}

/** A tune you opened before, as a loadable row: opening it again counts. */
function historyRow(
  ref: PlaylistTuneRef,
  inputs: TuneSourceInputs,
  prefix: string,
): TuneSourceEntry {
  const SOURCE_BY_KIND = {
    ireal: 'tune',
    score: 'native',
    openbook: 'openbook',
    realbook: 'realbook',
    saved: 'saved',
    library: 'library',
    account: 'shared',
  } as const;
  return withRecentRecording({
    ...playlistEntryRow(ref, inputs),
    key: `${prefix}-${playlistRefKey(ref)}`,
    source: ref.kind === 'ireal' && isAbcScheme(ref.scheme) ? 'abc' : SOURCE_BY_KIND[ref.kind],
  });
}

/** The home page's Most played shelf (state/tuneHistory.ts) as loadable
 *  rows, each with how long the band has played it. */
export function mostPlayedRows(
  inputs: TuneSourceInputs,
): { row: TuneSourceEntry; playSeconds: number }[] {
  const s = useStore.getState();
  return mostPlayedShelf(historyView(s.tuneHistory, s.otherHistory)).map((entry) => ({
    row: historyRow(entry.ref!, inputs, 'played'),
    playSeconds: entry.playSeconds,
  }));
}

/** The home page's Loved shelf (state/tuneTaste.ts), newest first: each
 *  loved tune, with a loadable row when it was loved from a tune you opened
 *  (a mark made on a Top pick names the library's tune, which a link opens). */
export function lovedRows(
  inputs: TuneSourceInputs,
): { mark: TasteMark; row: TuneSourceEntry | null }[] {
  return lovedTunes(useStore.getState().tuneTaste).map((mark) => ({
    mark,
    row: mark.ref ? historyRow(mark.ref, inputs, 'loved') : null,
  }));
}

/**
 * One playlist's rows, in the order the user arranged them.
 *
 * Separate from `collectTuneSources` (which flattens EVERY source to answer
 * "what tunes exist") because the Playlists page asks a narrower question and
 * must not pay for the openbook and realbook manifests to answer it. Rows
 * resolve through the same `playlistEntryRow` machinery, so a set list and the
 * Tunes browser can never disagree about what a row is.
 */
export function playlistRows(list: PlaylistDoc, inputs: TuneSourceInputs): TuneSourceEntry[] {
  return list.entries.map((entry) =>
    withRecentRecording({
      ...playlistEntryRow(entry.ref, inputs),
      key: `pl-${list.id}-${entry.entryId}`,
      context: list.name,
      source: 'playlist',
      playlistId: list.id,
      playlistEntryId: entry.entryId,
      ...(entry.note ? { note: entry.note } : {}),
    }),
  );
}

/** A playlist entry's row fields — the ref decides how it loads and whether
 * it carries a melody. A dangling saved ref (the Score was deleted) still
 * renders but reports the problem when loaded.
 *
 * A corpus ref stores only id/file/title, so its `lyrics` flag is looked up in
 * the live manifest rather than copied into the playlist when the tune was
 * added: a stored copy would go stale the moment the corpus is re-validated,
 * and a playlist row must agree with the same tune's row in the browser. When
 * the manifest has not been fetched the flag reads false — the same
 * conservative default the badge uses everywhere else. */
function playlistEntryRow(
  ref: PlaylistTuneRef,
  inputs: TuneSourceInputs,
): Omit<TuneSourceEntry, 'key' | 'context' | 'source' | 'playlistId'> {
  const savedCharts = inputs.savedCharts;
  if (ref.kind === 'ireal') {
    const meta = peekTuneMeta(ref.scheme, ref.chunk);
    return {
      title: ref.title,
      composer: ref.composer || undefined,
      melody: meta.melody,
      lyrics: meta.lyrics,
      style: meta.style,
      musicKey: meta.key,
      ref,
      // Nothing to go missing: the ref CARRIES its chunk (an iReal song or,
      // under the 'abc' scheme, one ABC tune) rather than pointing at one.
      resolvable: 'ok',
      load: () => loadChunk(ref.scheme, ref.chunk),
    };
  }
  if (ref.kind === 'openbook') {
    return {
      title: ref.title,
      composer: ref.composer,
      melody: true,
      lyrics: inputs.openbook.find((e) => e.id === ref.id)?.lyrics ?? false,
      ref,
      resolvable: manifestHas(inputs.openbook, ref.id),
      load: async () => {
        try {
          useStore
            .getState()
            .loadScore(await loadOpenbookScore({ id: ref.id, file: ref.file, title: ref.title }));
        } catch {
          throw new Error('Could not load that tune.');
        }
        return ref.title;
      },
    };
  }
  if (ref.kind === 'realbook') {
    return {
      title: ref.title,
      composer: ref.composer,
      melody: true,
      lyrics: (inputs.realbook ?? []).find((e) => e.id === ref.id)?.lyrics ?? false,
      ref,
      resolvable: manifestHas(inputs.realbook ?? [], ref.id),
      load: async () => {
        try {
          useStore.getState().loadScore(await loadRealbookScore({ id: ref.id, file: ref.file }));
        } catch {
          throw new Error('Could not load that tune.');
        }
        return ref.title;
      },
    };
  }
  if (ref.kind === 'library') {
    const entry = (inputs.library ?? []).find((e) => e.id === ref.id);
    return {
      title: ref.title,
      composer: ref.composer,
      melody: entry?.melody ?? false,
      lyrics: false,
      style: entry?.style,
      ref,
      resolvable: manifestHas(inputs.library ?? [], ref.id),
      load: () => loadLibraryRow(ref.id, ref.title),
    };
  }
  if (ref.kind === 'account') {
    // The author's own tune opens from their device; anyone else's is fetched
    // from the author's account, and only the fetch can say whether it is
    // still shared.
    const local = savedCharts.find((s) => s.id === ref.id);
    return {
      title: ref.title,
      composer: ref.composer,
      melody: local ? scoreHasMelody(local) : false,
      lyrics: local ? scoreHasLyrics(local) : false,
      style: local?.style,
      musicKey: local ? keyLabelOf(local.form[0]?.keySig) : undefined,
      ref,
      resolvable: local ? 'ok' : 'unknown',
      load: async () => {
        if (local) {
          useStore.getState().loadScore(local);
          return local.title;
        }
        let score: Score;
        try {
          score = await loadAccountTune(ref);
        } catch {
          throw new Error('Could not load that tune.');
        }
        useStore.getState().loadScore(score);
        return score.title || ref.title;
      },
    };
  }
  if (ref.kind === 'score') {
    const entry = bundledScoreById(ref.id);
    return {
      title: ref.title,
      composer: ref.composer,
      melody: entry?.summary.hasMelody ?? false,
      lyrics: entry?.summary.hasLyrics ?? false,
      style: entry?.summary.style,
      musicKey: entry?.summary.musicKey,
      ref,
      // Bundled scores ship with the build, so this is exactly what the
      // loader will find.
      resolvable: entry?.revision.id === ref.revisionId ? 'ok' : 'missing',
      scoreUpdate: latestPlaylistScoreRef(ref),
      load: async () => {
        useStore.getState().loadScore(await loadBundledScore(ref.id, ref.revisionId));
        return ref.title;
      },
    };
  }
  const saved = savedCharts.find((s) => s.id === ref.id);
  return {
    title: ref.title,
    composer: ref.composer,
    melody: saved ? scoreHasMelody(saved) : false,
    lyrics: saved ? scoreHasLyrics(saved) : false,
    style: saved?.style,
    musicKey: saved ? keyLabelOf(saved.form[0]?.keySig) : undefined,
    ref,
    // The same lookup the loader does, so this cannot disagree with it.
    resolvable: saved ? 'ok' : 'missing',
    load: () => {
      if (!saved) return Promise.reject(new Error('That saved tune no longer exists.'));
      useStore.getState().loadScore(saved);
      return Promise.resolve(saved.title);
    },
  };
}

/** "Composer · context" subtitle, matching the palette's historical format. */
export function tuneSubtitle(entry: TuneSourceEntry): string | undefined {
  const parts = [entry.composer, entry.context].filter(Boolean);
  return parts.length ? parts.join(' · ') : undefined;
}

/**
 * Which corpus the LOADED tune came from, read off the score's stable id —
 * the same identity that keys per-tune band memory, so it survives reloads
 * and tune URLs without the store having to record anything at load time.
 * `loadOpenbookScore`/`loadRealbookScore` stamp `openbook-`/`realbook-`
 * prefixes; a catalogue score's id is in BUNDLED_SCORES; a user's saved tune
 * is matched by MEMBERSHIP in savedCharts (its `chart-` uid prefix is shared
 * with presets and new tunes, so the prefix alone proves nothing — membership
 * is checked first so a saved copy of a corpus tune reads as saved). An iReal
 * chunk deliberately has no durable id (see tuneIdentityOf), so the bundled
 * playlists return null — no badge beats a guessed one.
 */
export function loadedTuneSource(
  score: Pick<Score, 'id'>,
  savedCharts: readonly Pick<Score, 'id'>[],
): TuneSourceKind | null {
  const id = score.id;
  if (!id) return null;
  if (savedCharts.some((saved) => saved.id === id)) return 'saved';
  if (id.startsWith('openbook-')) return 'openbook';
  if (id.startsWith('realbook-')) return 'realbook';
  if (id.startsWith('library-')) return 'library';
  if (bundledScoreById(id)) return 'native';
  return null;
}

/**
 * What a version of a tune IS, wherever a tune is badged: a score, a lead
 * sheet, a chord chart, your own copy. Never the collection it came from
 * (user ruling, Oct 2026: the screen does not name the borrowed collections).
 * An imported ABC tune says ABC, its notation, as an iReal Pro link would be
 * a chord chart.
 */
export function tuneVersionLabel(source: TuneSourceKind): string {
  switch (source) {
    case 'native':
      return 'Score';
    case 'realbook':
    case 'openbook':
      return 'Lead sheet';
    case 'tune':
      return 'Chord chart';
    case 'abc':
      return 'ABC';
    case 'saved':
      return 'Saved';
    case 'shared':
      return 'Shared';
    case 'library':
      return 'Library';
    case 'playlist':
      return 'Playlist';
    case 'preset':
      return 'Study';
  }
}
